import { useState } from 'react'
import { ChevronDown, Trash2, UserPlus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-provider'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  useChangeCollaboratorRole,
  useCollaborators,
  useInviteCollaborator,
  useRevokeCollaborator,
} from '@/features/collaboration/use-collaboration'
import type { ApiError, Collaborator, CollaboratorRole } from '@/api/types'

const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as ApiError | undefined)?.detail ?? fallback

interface Props {
  projectId: string
  trigger?: React.ReactNode
}

/**
 * Phase 18 — Owner-facing collaborator management drawer. Lists all collaborators (PENDING +
 * ACCEPTED + DECLINED + REVOKED), surfaces invite form, allows role-flip on active members.
 */
export const CollaboratorsDrawer = ({ projectId, trigger }: Props) => {
  const query = useCollaborators(projectId)
  const invite = useInviteCollaborator(projectId)
  const revoke = useRevokeCollaborator(projectId)
  const changeRole = useChangeCollaboratorRole(projectId)
  const confirm = useConfirm()
  const [handle, setHandle] = useState('')
  const [role, setRole] = useState<CollaboratorRole>('EDITOR')

  const collaborators = query.data ?? []
  const active = collaborators.filter((c) => c.state === 'ACCEPTED')
  const pending = collaborators.filter((c) => c.state === 'PENDING')
  const past = collaborators.filter((c) => c.state === 'DECLINED' || c.state === 'REVOKED')

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = handle.trim()
    if (!trimmed) return
    invite.mutate(
      { handle: trimmed, role },
      {
        onSuccess: () => {
          setHandle('')
          toast.success(`Invited @${trimmed} as ${role}`)
        },
        onError: (e) => toast.error(apiErrorMessage(e, 'Failed to invite collaborator')),
      }
    )
  }

  const handleRevoke = async (c: Collaborator) => {
    const ok = await confirm({
      title: `Revoke access for @${c.handle ?? 'this user'}?`,
      description:
        'They will no longer be able to view or edit this project. You can re-invite them later.',
      confirmText: 'Revoke',
      destructive: true,
    })
    if (!ok) return
    revoke.mutate(c.userId, {
      onSuccess: () => toast.success('Access revoked'),
      onError: (e) => toast.error(apiErrorMessage(e, 'Failed to revoke access')),
    })
  }

  const handleChangeRole = (c: Collaborator, next: CollaboratorRole) => {
    if (c.role === next) return
    changeRole.mutate(
      { userId: c.userId, request: { role: next } },
      {
        onSuccess: () => toast.success(`Role changed to ${next.toLowerCase()}`),
        onError: (e) => toast.error(apiErrorMessage(e, 'Failed to change role')),
      }
    )
  }

  return (
    <Sheet>
      <SheetTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="gap-2">
            <UserPlus className="h-4 w-4" />
            Collaborators
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Collaborators · {active.length}</SheetTitle>
          <SheetDescription>
            Invite Users to edit or view this project. Only the Owner can manage collaborators.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <form onSubmit={handleInvite} className="space-y-2 rounded border p-3">
            <div className="grid grid-cols-[1fr_5rem] gap-2">
              <Input
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="handle (e.g. alice)"
                className="h-8 text-sm"
                aria-label="Invitee handle"
              />
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as CollaboratorRole)}
                className="h-8 rounded border bg-background px-2 text-sm"
                aria-label="Role"
              >
                <option value="EDITOR">Editor</option>
                <option value="VIEWER">Viewer</option>
              </select>
            </div>
            <Button
              type="submit"
              size="sm"
              className="gap-1.5"
              disabled={invite.isPending || !handle.trim()}
            >
              <UserPlus className="h-3.5 w-3.5" />
              Send invite
            </Button>
          </form>

          <CollaboratorSection
            title="Active"
            rows={active}
            onRevoke={handleRevoke}
            onChangeRole={handleChangeRole}
          />
          <CollaboratorSection
            title="Pending invitations"
            rows={pending}
            onRevoke={handleRevoke}
            onChangeRole={null}
          />
          <CollaboratorSection
            title="Past collaborators"
            rows={past}
            onRevoke={null}
            onChangeRole={null}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}

interface SectionProps {
  title: string
  rows: Collaborator[]
  onRevoke: ((c: Collaborator) => void) | null
  onChangeRole: ((c: Collaborator, next: CollaboratorRole) => void) | null
}

const CollaboratorSection = ({ title, rows, onRevoke, onChangeRole }: SectionProps) => {
  if (rows.length === 0) return null
  return (
    <section>
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h3>
      <ul className="mt-2 space-y-1">
        {rows.map((c) => (
          <li key={c.userId} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex-1 truncate">
              <span className="font-medium">@{c.handle ?? '?'}</span>
              {c.displayName && (
                <span className="ml-1 text-muted-foreground">· {c.displayName}</span>
              )}
            </span>
            {onChangeRole ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs uppercase text-muted-foreground hover:text-foreground"
                    aria-label={`Change role for ${c.handle ?? 'collaborator'}`}
                  >
                    {c.role}
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => onChangeRole(c, 'EDITOR')}>
                    Editor
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onChangeRole(c, 'VIEWER')}>
                    Viewer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <span className="text-xs uppercase text-muted-foreground">{c.role}</span>
            )}
            {onRevoke && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={() => onRevoke(c)}
                aria-label={`Revoke ${c.handle ?? 'collaborator'}'s access`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
